import { Accordion, Button, IconButton, Modal, Typography } from '@mui/material';
import React, { forwardRef, memo, useImperativeHandle, useState } from 'react';
import {
  CloseModalWrapper, ExportReportWrapper, ModalWrapper, MainExportContent,
  SwitchWrapper, FooterWrapper, ButtonWrapper, AccordionSummaryExport, AccordionDetailsExport,
} from './ExportPdfModal.styled';
import { ReactComponent as XIcon } from '../../../assets/icons/base/x.svg';
import { ReactComponent as ChevronRightIcon } from '../../../assets/icons/base/chevron-right.svg';
import { Switch } from '../../../components/common/Switch';
import { BasicInput } from '../../../components/common/InputDropdown';
import { Box } from '@mui/system';
import { Radio } from '../../../components/common/Radio';
import * as htmlToImage from 'html-to-image';
import { pdf } from '@react-pdf/renderer';
import PdfDocument from './PdfDocument';
import html2canvas from 'html2canvas';
import { AggregatedAlertsData, AggregatedReadsData, DataForShare, PickedFilters } from '../Charts.types';
import moment from 'moment';
import { LoadingButton } from '@mui/lab';
import { saveAs } from 'file-saver';
import { addSnackbar } from '../../../redux/slices/snackbar';
import { useDispatch } from 'react-redux';
import api from '../../../api/base';
import { reportCharts } from '../../../api/paths';
import { initDataForShare } from '../Charts.constants';
import useError from '../../../hooks/useError';

export type ExportPdfModalRefType = {
  openModal: () => void;
};

type ExportPdfModalType = {
  duration: string;
  alertsStats: Array<Record<string, string | number>>;
  readsStats: Array<Record<string, string | number>>;
  readsData: AggregatedReadsData;
  alertsData: AggregatedAlertsData;
  pickedFilters: PickedFilters;
  readsMode: 'site' | 'camera';
  alertsMode: 'alert' | 'site' | 'camera';
};

const ExportPdfModal = forwardRef<ExportPdfModalRefType, ExportPdfModalType>((props, ref) => {
  const {
    duration, alertsStats, readsStats, readsData, alertsData, pickedFilters, readsMode, alertsMode,
  } = props;

  const dispatch = useDispatch();
  const [handleError] = useError();

  const [open, setOpen] = useState<boolean>(false);
  const [expandedAccord, setExpandedAccord] = useState<'export' | 'share'>('export');

  const [dataForExport, setDataForExport] = useState<DataForShare>(initDataForShare);
  const [dataForShare, setDataForShare] = useState<DataForShare>(initDataForShare);
  const [emails, setEmails] = useState<string>('');

  const [exportFormat, setExportFormat] = useState<'pdf' | 'csv' | 'xls'>('pdf');
  const [exportLoading, setExportLoading] = useState<boolean>(false);
  const [shareLoading, setShareLoading] = useState<boolean>(false);

  useImperativeHandle(
    ref,
    () => ({
      openModal: () => {
        setOpen(true);
      },
    }),
  );

  const handleClose = () => {
    setOpen(false);
    setExportLoading(false);
    setEmails('');
  };

  const getPdfData = async (share?: boolean, options?: DataForShare) => {
    setExportLoading(true);
    const chartsReads = document.getElementById('chart-Reads');
    const chartsAlerts = document.getElementById('chart-Alerts');
    let graphReadsImg = '';
    let graphAlertsImg = '';
    if (options?.graph) {
      graphReadsImg = await htmlToImage.toPng(chartsReads!);
      graphAlertsImg = await htmlToImage.toPng(chartsAlerts!);
    }

    let mapAlertsImg = '';
    let mapReadsImg = '';
    if (options?.map) {
      const mapAlerts = document.getElementById('map-alerts');
      mapAlertsImg = (await html2canvas(mapAlerts!, { useCORS: true })).toDataURL();
      const mapReads = document.getElementById('map-reads');
      mapReadsImg = (await html2canvas(mapReads!, { useCORS: true })).toDataURL();
    }

    const dateFrom = (pickedFilters.fromDate ? moment(pickedFilters.fromDate) : moment(Date.now()).subtract(duration.slice(0, 2), 'days'))
      .format('MMM Do, YY');
    const toDate = (pickedFilters.toDate ? moment(pickedFilters.toDate) : moment(Date.now())).format('MMM Do, YY');
    const doc = (
      <PdfDocument
        dateTo={toDate}
        dateFrom={dateFrom}
        includeGraphs={options?.graph}
        includeMaps={options?.map}
        includeTable={options?.table}
        graphAlertsImg={graphAlertsImg}
        graphReadsImg={graphReadsImg!}
        mapAlertsImg={mapAlertsImg!}
        mapReadsImg={mapReadsImg!}
        alertsStats={alertsStats}
        readsStats={readsStats}
        readsData={readsData}
        alertsData={alertsData}
        readsMode={readsMode}
        alertsMode={alertsMode}
      />
    );
      // {} is important, throws without an argument
    const asPdf = pdf([] as any);
    asPdf.updateContainer(doc);
    const blob = await asPdf.toBlob();

    setExportLoading(false);

    if (share) {
      return blob;
    }
    saveAs(blob, `vipr-report-${moment().format('MMM-Do-YY')}.pdf`);

    return null;
  };

  const exportHandler = () => {
    if (exportFormat === 'pdf') getPdfData(false, dataForExport);
  };

  const handleExportFormatChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setExportFormat((event.target as HTMLInputElement).value as 'pdf' | 'csv' | 'xls');
  };

  const handleDataForExportChange = (e: React.ChangeEvent<HTMLInputElement>, field: 'table' | 'graph' | 'map') => {
    if (e.target.checked && field !== 'table') setExportFormat('pdf');
    setDataForExport((dfe) => ({ ...dfe, [field]: e.target.checked }));
  };

  const handleDataForShareChange = (e: React.ChangeEvent<HTMLInputElement>, field: 'table' | 'graph' | 'map') => {
    setDataForShare((dfs) => ({ ...dfs, [field]: e.target.checked }));
  };

  const handleEmailsChange = (e: any) => {
    const { value } = e.target;
    setEmails(value);
  };

  const sharePdf = async () => {
    setShareLoading(true);
    try {
      const formData = new FormData();

      const blobOfFile = await getPdfData(true, dataForShare);
      formData.append('file', blobOfFile as Blob, `vipr-report-${moment().format('MMM-Do-YY')}.pdf`);
      formData.append('emails', emails);

      await api.post(reportCharts, formData, {
        headers: {
          'content-type': 'multipart/form-data',
        },
      });
      dispatch(addSnackbar({ message: 'Report Shared!', options: { variant: 'success' } }));
    } catch (error: any) {
      handleError(error);
    }
    setShareLoading(false);
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="ExportPdfModal"
      aria-describedby="ExportPdfModal"
    >
      <ModalWrapper>
        <CloseModalWrapper>
          <div />
          <IconButton sx={{ color: 'white' }} onClick={handleClose}>
            <XIcon style={{ height: '20px', width: '20px' }} />
          </IconButton>
        </CloseModalWrapper>
        <ExportReportWrapper>
          <Accordion
            expanded={expandedAccord === 'export'}
            onChange={() => setExpandedAccord('export')}
            sx={{ width: '100%', border: 'none !important', borderRadius: '0 !important', boxShadow: 'none' }}
          >
            <AccordionSummaryExport
              id="export-report"
              aria-controls="export-report-content"
              expandIcon={expandedAccord !== 'export' && <ChevronRightIcon />}
            >
              <Typography variant="tlg" fontWeight="500" color="grey.900">
                Export Report
              </Typography>
            </AccordionSummaryExport>
            <AccordionDetailsExport>
              <MainExportContent>
                <Typography variant="tsm" fontWeight="400" color="grey.500">
                  Export the data shown into a pdf.
                </Typography>
                <SwitchWrapper>
                  <Typography color="grey.700" variant="tmd" fontWeight="500">
                    Area Graph
                  </Typography>
                  <Switch
                    checked={dataForExport.graph}
                    value={dataForExport.graph}
                    onChange={(e) => handleDataForExportChange(e, 'graph')}
                  />
                </SwitchWrapper>
                <SwitchWrapper>
                  <Typography color="grey.700" variant="tmd" fontWeight="500">
                    Table View
                  </Typography>
                  <Switch
                    checked={dataForExport.table}
                    value={dataForExport.table}
                    onChange={(e) => handleDataForExportChange(e, 'table')}
                  />
                </SwitchWrapper>
                <SwitchWrapper>
                  <Typography color="grey.700" variant="tmd" fontWeight="500">
                    Heatmap
                  </Typography>
                  <Switch
                    checked={dataForExport.map}
                    value={dataForExport.map}
                    onChange={(e) => handleDataForExportChange(e, 'map')}
                  />
                </SwitchWrapper>
              </MainExportContent>
              <FooterWrapper mt="32px">
                <Box>
                  <Typography color="grey.700" variant="tmd" fontWeight="500">
                    Export as:
                  </Typography>
                  <Box display="flex" flexDirection="row" ml="-12px">
                    <Box display="flex" alignItems="center">
                      <Radio
                        onChange={handleExportFormatChange}
                        value="pdf"
                        checked={exportFormat === 'pdf'}
                      />
                      <Typography variant="tsm" fontWeight="500" color="grey.700">
                        PDF
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center">
                      <Radio
                        onChange={handleExportFormatChange}
                        value="csv"
                        checked={exportFormat === 'csv'}
                        disabled={dataForExport.map || dataForExport.graph}
                      />
                      <Typography variant="tsm" fontWeight="500" color="grey.700">
                        CSV
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center">
                      <Radio
                        onChange={handleExportFormatChange}
                        value="xls"
                        checked={exportFormat === 'xls'}
                        disabled={dataForExport.map || dataForExport.graph}
                      />
                      <Typography variant="tsm" fontWeight="500" color="grey.700">
                        XLS
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                {(dataForExport.graph || dataForExport.map) && (
                  <Typography
                    component="p"
                    variant="tsm"
                    color="grey.500"
                  >
                    Line Graph and Heatmap aren’t available for CSV and XLS export.
                  </Typography>
                )}
                {(dataForExport.table && exportFormat === 'pdf') && (
                  <Typography
                    component="p"
                    variant="tsm"
                    color="grey.500"
                  >
                    Only a max of 60 rows will be exported for the table in PDF format.
                  </Typography>
                )}
                <ButtonWrapper>
                  <Button
                    sx={{ flexGrow: 1 }}
                    variant="outlined"
                    size="large"
                    color="primary"
                    onClick={handleClose}
                  >
                    Cancel
                  </Button>
                  <LoadingButton
                    sx={{ flexGrow: 1 }}
                    variant="contained"
                    size="large"
                    color="primary"
                    onClick={exportHandler}
                    loading={exportLoading}
                  >
                    Export
                  </LoadingButton>
                </ButtonWrapper>
              </FooterWrapper>
            </AccordionDetailsExport>
          </Accordion>
        </ExportReportWrapper>
        <ExportReportWrapper>
          <Accordion
            expanded={expandedAccord === 'share'}
            onChange={() => setExpandedAccord('share')}
            sx={{ width: '100%', border: 'none !important', borderRadius: '0 !important', boxShadow: 'none' }}
          >
            <AccordionSummaryExport
              id="share-report"
              aria-controls="share-report-content"
              expandIcon={expandedAccord !== 'share' && <ChevronRightIcon />}
            >
              <Typography variant="tlg" fontWeight="500" color="grey.900">
                Share Report
              </Typography>
            </AccordionSummaryExport>
            <AccordionDetailsExport>
              <MainExportContent>
                <Typography variant="tsm" fontWeight="400" color="grey.500">
                  Share this report via email with anyone you want.
                </Typography>
                <SwitchWrapper>
                  <Typography color="grey.700" variant="tmd" fontWeight="500">
                    Line Graph
                  </Typography>
                  <Switch
                    checked={dataForShare.graph}
                    value={dataForShare.graph}
                    onChange={(e) => handleDataForShareChange(e, 'graph')}
                  />
                </SwitchWrapper>
                <SwitchWrapper>
                  <Typography color="grey.700" variant="tmd" fontWeight="500">
                    Table View
                  </Typography>
                  <Switch
                    checked={dataForShare.table}
                    value={dataForShare.table}
                    onChange={(e) => handleDataForShareChange(e, 'table')}
                  />
                </SwitchWrapper>
                <SwitchWrapper>
                  <Typography color="grey.700" variant="tmd" fontWeight="500">
                    Heatmap
                  </Typography>
                  <Switch
                    checked={dataForShare.map}
                    value={dataForShare.map}
                    onChange={(e) => handleDataForShareChange(e, 'map')}
                  />
                </SwitchWrapper>
                <BasicInput
                  value={emails}
                  onChange={handleEmailsChange}
                  placeholder="Add emails separated by a comma"
                  fullWidth
                />
              </MainExportContent>
              <FooterWrapper mt="25px">
                <ButtonWrapper>
                  <Button
                    sx={{ flexGrow: 1 }}
                    variant="outlined"
                    size="large"
                    color="primary"
                    onClick={handleClose}
                  >
                    Cancel
                  </Button>
                  <LoadingButton
                    sx={{ flexGrow: 1 }}
                    variant="contained"
                    size="large"
                    color="primary"
                    loading={shareLoading}
                    onClick={sharePdf}
                  >
                    Share
                  </LoadingButton>
                </ButtonWrapper>
              </FooterWrapper>
            </AccordionDetailsExport>
          </Accordion>
        </ExportReportWrapper>
      </ModalWrapper>
    </Modal>
  );
});

export default memo(ExportPdfModal);
